import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { MetadataEntry } from '../../types/MetadataEntry';
import { MetadataEntryKind } from '../../types/MetadataEntryKind';
import { MetadataValueKind } from '../../types/MetadataValueKind';
import {
  metadataEntryKindLabel,
  metadataValueKindLabel,
} from '../../utils/jsonMetadata';
import { MetadataEditor } from './MetadataEditor';

type MetadataEntryEditorProps = {
  entry: MetadataEntry;
  onChange: (entry: MetadataEntry) => void;
  onRemove: () => void;
  disabled?: boolean;
  depth?: number;
  itemLabel?: string;
};

export function MetadataEntryEditor({
  entry,
  onChange,
  onRemove,
  disabled = false,
  depth = 0,
  itemLabel,
}: MetadataEntryEditorProps) {
  const showKeyInput = !itemLabel;

  function updateKind(nextKind: MetadataEntryKind): void {
    if (nextKind === entry.kind) {
      return;
    }

    if (nextKind === 'object') {
      onChange({
        ...entry,
        kind: 'object',
        children: entry.children ?? [],
        valueKind: undefined,
        scalarValue: undefined,
        listItems: undefined,
        listObjects: undefined,
        listItemKind: undefined,
      });
      return;
    }

    if (nextKind === 'list') {
      onChange({
        ...entry,
        kind: 'list',
        listItemKind: 'text',
        listItems: entry.listItems?.length ? entry.listItems : [''],
        children: undefined,
        valueKind: undefined,
        scalarValue: undefined,
        listObjects: undefined,
      });
      return;
    }

    onChange({
      ...entry,
      kind: 'value',
      valueKind: entry.valueKind ?? 'string',
      scalarValue: entry.scalarValue ?? '',
      children: undefined,
      listItems: undefined,
      listObjects: undefined,
      listItemKind: undefined,
    });
  }

  function updateValueKind(nextValueKind: MetadataValueKind): void {
    onChange({
      ...entry,
      valueKind: nextValueKind,
      scalarValue:
        nextValueKind === 'boolean'
          ? entry.scalarValue === 'true'
            ? 'true'
            : 'false'
          : (entry.scalarValue ?? ''),
    });
  }

  return (
    <div
      className={`rounded-xl border border-secondary-200 bg-white ${
        depth > 0 ? 'metadata-entry-nested' : ''
      }`}
    >
      <div className="flex flex-wrap items-start gap-2 p-3">
        <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-12">
          {showKeyInput ? (
            <label className="sm:col-span-4">
              <span className="mb-1 block text-xs font-medium text-secondary-600">Chave</span>
              <input
                className="erp-input-compact w-full"
                value={entry.key}
                disabled={disabled}
                placeholder="ex.: employeeRange"
                onChange={(event) => onChange({ ...entry, key: event.target.value })}
              />
            </label>
          ) : (
            <div className="sm:col-span-4">
              <span className="mb-1 block text-xs font-medium text-secondary-600">Item</span>
              <p className="rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-secondary-700">
                {itemLabel}
              </p>
            </div>
          )}

          <label className="sm:col-span-3">
            <span className="mb-1 block text-xs font-medium text-secondary-600">Tipo</span>
            <select
              className="erp-input-compact w-full"
              value={entry.kind}
              disabled={disabled}
              onChange={(event) => updateKind(event.target.value as MetadataEntryKind)}
            >
              <option value="value">{metadataEntryKindLabel('value')}</option>
              <option value="list">{metadataEntryKindLabel('list')}</option>
              <option value="object">{metadataEntryKindLabel('object')}</option>
            </select>
          </label>

          {entry.kind === 'value' ? (
            <>
              <label className="sm:col-span-2">
                <span className="mb-1 block text-xs font-medium text-secondary-600">Formato</span>
                <select
                  className="erp-input-compact w-full"
                  value={entry.valueKind ?? 'string'}
                  disabled={disabled}
                  onChange={(event) => updateValueKind(event.target.value as MetadataValueKind)}
                >
                  <option value="string">{metadataValueKindLabel('string')}</option>
                  <option value="number">{metadataValueKindLabel('number')}</option>
                  <option value="boolean">{metadataValueKindLabel('boolean')}</option>
                </select>
              </label>
              <label className="sm:col-span-3">
                <span className="mb-1 block text-xs font-medium text-secondary-600">Valor</span>
                {entry.valueKind === 'boolean' ? (
                  <select
                    className="erp-input-compact w-full"
                    value={entry.scalarValue === 'true' ? 'true' : 'false'}
                    disabled={disabled}
                    onChange={(event) => onChange({ ...entry, scalarValue: event.target.value })}
                  >
                    <option value="true">Sim</option>
                    <option value="false">Não</option>
                  </select>
                ) : (
                  <input
                    className="erp-input-compact w-full"
                    type={entry.valueKind === 'number' ? 'number' : 'text'}
                    value={entry.scalarValue ?? ''}
                    disabled={disabled}
                    placeholder="Valor"
                    onChange={(event) => onChange({ ...entry, scalarValue: event.target.value })}
                  />
                )}
              </label>
            </>
          ) : null}
        </div>

        <button
          type="button"
          className="mt-6 rounded-lg p-2 text-secondary-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
          disabled={disabled}
          title="Remover campo"
          aria-label="Remover campo"
          onClick={onRemove}
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>

      {entry.kind === 'list' ? (
        <div className="space-y-3 border-t border-secondary-100 px-3 pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-medium text-secondary-600">Itens da lista</p>
            <select
              className="erp-input-compact text-xs"
              value={entry.listItemKind ?? 'text'}
              disabled={disabled}
              onChange={(event) => {
                const listItemKind = event.target.value as 'text' | 'object';
                onChange({
                  ...entry,
                  listItemKind,
                  listItems: listItemKind === 'text' ? entry.listItems ?? [''] : undefined,
                  listObjects:
                    listItemKind === 'object'
                      ? entry.listObjects?.length
                        ? entry.listObjects
                        : [[]]
                      : undefined,
                });
              }}
            >
              <option value="text">Texto</option>
              <option value="object">Grupos de campos</option>
            </select>
          </div>

          {entry.listItemKind === 'object' ? (
            <div className="space-y-3">
              {(entry.listObjects ?? []).map((objectFields, index) => (
                <div key={`${entry.id}-object-${index}`} className="rounded-lg border border-secondary-100 bg-secondary-50/60 p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-secondary-700">Grupo {index + 1}</p>
                    <button
                      type="button"
                      className="text-xs text-red-600 hover:underline disabled:opacity-50"
                      disabled={disabled}
                      onClick={() => {
                        const nextObjects = [...(entry.listObjects ?? [])];
                        nextObjects.splice(index, 1);
                        onChange({ ...entry, listObjects: nextObjects });
                      }}
                    >
                      Remover grupo
                    </button>
                  </div>
                  <MetadataEditor
                    entries={objectFields}
                    disabled={disabled}
                    depth={depth + 1}
                    onChange={(nextFields) => {
                      const nextObjects = [...(entry.listObjects ?? [])];
                      nextObjects[index] = nextFields;
                      onChange({ ...entry, listObjects: nextObjects });
                    }}
                  />
                </div>
              ))}
              <button
                type="button"
                className="erp-btn-secondary erp-btn-sm"
                disabled={disabled}
                onClick={() =>
                  onChange({
                    ...entry,
                    listObjects: [...(entry.listObjects ?? []), []],
                  })
                }
              >
                Adicionar grupo
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {(entry.listItems ?? []).map((item, index) => (
                <div key={`${entry.id}-item-${index}`} className="flex items-center gap-2">
                  <input
                    className="erp-input-compact min-w-0 flex-1"
                    value={item}
                    disabled={disabled}
                    placeholder={`Item ${index + 1}`}
                    onChange={(event) => {
                      const nextItems = [...(entry.listItems ?? [])];
                      nextItems[index] = event.target.value;
                      onChange({ ...entry, listItems: nextItems });
                    }}
                  />
                  <button
                    type="button"
                    className="rounded-lg p-2 text-secondary-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    disabled={disabled || (entry.listItems?.length ?? 0) <= 1}
                    title="Remover item"
                    aria-label="Remover item"
                    onClick={() => {
                      const nextItems = [...(entry.listItems ?? [])];
                      nextItems.splice(index, 1);
                      onChange({ ...entry, listItems: nextItems.length ? nextItems : [''] });
                    }}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-primary-800 disabled:opacity-50"
                disabled={disabled}
                onClick={() => onChange({ ...entry, listItems: [...(entry.listItems ?? []), ''] })}
              >
                <PlusIcon className="h-4 w-4" />
                Adicionar item
              </button>
            </div>
          )}
        </div>
      ) : null}

      {entry.kind === 'object' ? (
        <div className="border-t border-secondary-100 px-3 pb-3">
          <p className="mb-2 pt-3 text-xs font-medium text-secondary-600">Campos do grupo</p>
          <MetadataEditor
            entries={entry.children ?? []}
            disabled={disabled}
            depth={depth + 1}
            onChange={(children) => onChange({ ...entry, children })}
          />
        </div>
      ) : null}
    </div>
  );
}
